import React, { useState, useEffect } from 'react';
import styles from './AddSalaryModal.module.css';
import { toast } from 'react-toastify';
import { db } from '../../../config/firebaseConfig';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { FaMoneyBillWave, FaSave, FaTimes, FaCalculator } from 'react-icons/fa';
import { useTheme } from '../../../contexts/ThemeContext';

const formatCurrency = (amount) => {
    if (!amount) return '0 ₺';
    return new Intl.NumberFormat('tr-TR', { 
        style: 'currency', 
        currency: 'TRY' 
    }).format(amount).replace('₺', '') + ' ₺';
};

const AddSalaryModal = ({ onClose, onSalaryAdded, onSalaryUpdated, employeeId, editingSalary }) => {
    const { isDarkMode } = useTheme();
    const [formData, setFormData] = useState({
        contractDate: '',
        grossSalary: '',
        netSalary: '',
        totalDeductions: '',
        suggestedCost: ''
    });

    useEffect(() => {
        if (editingSalary) {
            setFormData({
                contractDate: editingSalary.contractDate || '',
                grossSalary: editingSalary.grossSalary || '',
                netSalary: editingSalary.netSalary || '',
                totalDeductions: editingSalary.totalDeductions || '',
                suggestedCost: editingSalary.suggestedCost || ''
            });
        }
    }, [editingSalary]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (name === 'netSalary') {
            const calculations = calculateGrossFromNet(Number(value));
            setFormData(prev => ({
                ...prev,
                grossSalary: calculations.grossSalary,
                totalDeductions: calculations.totalDeductions,
                suggestedCost: calculations.totalEmployerCost
            }));
        }
    };

    const calculateGrossFromNet = (targetNet) => {
        if (!targetNet || targetNet <= 0) {
            return {
                grossSalary: '',
                totalDeductions: '',
                totalEmployerCost: ''
            };
        }

        let low = targetNet;  // Minimum possible gross (net amount)
        let high = targetNet * 2;  // Maximum possible gross (reasonable upper limit)
        let grossSalary = (low + high) / 2;
        let iterations = 0;
        const maxIterations = 30;  // Prevent infinite loops
        
        while (iterations < maxIterations) {
            const calculations = calculateCosts(grossSalary);
            const currentNet = parseFloat(calculations.netSalary);
            
            // If we're within 0.01 TL, we've found our answer
            if (Math.abs(currentNet - targetNet) < 0.01) {
                return {
                    grossSalary: grossSalary.toFixed(2),
                    totalDeductions: calculations.totalDeductions,
                    totalEmployerCost: calculations.totalEmployerCost,
                };
            }
            
            // Binary search adjustment
            if (currentNet > targetNet) {
                high = grossSalary;
            } else {
                low = grossSalary;
            }
            
            grossSalary = (low + high) / 2;
            iterations++;
        }
        
        // Return best approximation if exact match not found
        const finalCalculations = calculateCosts(grossSalary);
        return {
            grossSalary: grossSalary.toFixed(2),
            totalDeductions: finalCalculations.totalDeductions,
            totalEmployerCost: finalCalculations.totalEmployerCost,
        };
    };

    const calculateCosts = (grossSalary) => {
        const sskEmployeeShare = grossSalary * 0.14; // %14
        const unemploymentEmployeeShare = grossSalary * 0.01; // %1
        const stampTax = grossSalary * 0.00759; // %0.759

        const incomeTaxBase = grossSalary - (sskEmployeeShare + unemploymentEmployeeShare);
        let incomeTax = 0;

        if (incomeTaxBase <= 24000) {
            incomeTax = incomeTaxBase * 0.15;
        } else if (incomeTaxBase <= 53000) {
            incomeTax = 3600 + (incomeTaxBase - 24000) * 0.20;
        } else if (incomeTaxBase <= 190000) {
            incomeTax = 9400 + (incomeTaxBase - 53000) * 0.27;
        } else {
            incomeTax = 46390 + (incomeTaxBase - 190000) * 0.35;
        }

        const totalDeductions = sskEmployeeShare + unemploymentEmployeeShare + incomeTax + stampTax;
        const netSalary = grossSalary - totalDeductions;

        const sskEmployerShare = grossSalary * 0.155; // %15.5
        const unemploymentEmployerShare = grossSalary * 0.02; // %2
        const totalEmployerCost = grossSalary + sskEmployerShare + unemploymentEmployerShare;

        return {
            netSalary: netSalary.toFixed(2),
            totalDeductions: totalDeductions.toFixed(2),
            totalEmployerCost: totalEmployerCost.toFixed(2),
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.contractDate || !formData.netSalary) {
            toast.error('Lütfen tüm zorunlu alanları doldurun!');
            return;
        }

        try {
            const timestamp = new Date().getTime();
            const salaryData = {
                employeeId,
                ...formData,
                contractDate: formData.contractDate
            };

            if (editingSalary && editingSalary.id) {
                // Update existing salary
                const salaryRef = doc(db, 'Salaries', editingSalary.id);
                await updateDoc(salaryRef, { ...salaryData, updatedAt: timestamp });
                onSalaryUpdated({ id: editingSalary.id, ...salaryData, updatedAt: timestamp });
                toast.success('Maaş bilgisi başarıyla güncellendi!');
            } else {
                // Add new salary
                const newSalaryData = { ...salaryData, createdAt: timestamp };
                const docRef = await addDoc(collection(db, 'Salaries'), newSalaryData);
                onSalaryAdded({ id: docRef.id, ...newSalaryData });
                toast.success('Maaş bilgisi başarıyla eklendi!');
            }

            onClose();
        } catch (error) {
            console.error('Maaş işlenirken hata oluştu:', error);
            toast.error('Maaş işlenirken bir hata oluştu!');
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        <FaMoneyBillWave className={styles.modalIcon} />
                        {editingSalary ? 'Maaş Bilgisini Düzenle' : 'Yeni Maaş Ekle'}
                    </h2>
                    <button 
                        className={styles.closeButton} 
                        onClick={onClose}
                        aria-label="Kapat"
                    >
                        <FaTimes />
                    </button>
                </div>
                
                <div className={styles.modalBody}>
                    <form onSubmit={handleSubmit} className={styles.salaryForm} id="add-salary-form">
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Sözleşme Tarihi:</label>
                            <input
                                type="date"
                                name="contractDate"
                                value={formData.contractDate}
                                onChange={handleChange}
                                className={styles.formInput}
                                required
                            />
                        </div>
                        
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Net Ücret (TL):</label>
                            <input
                                type="number"
                                name="netSalary"
                                value={formData.netSalary}
                                onChange={handleChange}
                                className={`${styles.formInput} ${styles.inputNumber}`}
                                placeholder="Çalışanın eline geçecek net tutarı girin"
                                required
                                min="0"
                                step="0.01"
                            />
                        </div>
                        
                        {formData.grossSalary && (
                            <div className={styles.infoBox}>
                                <div className={styles.infoItem}>
                                    <span className={styles.infoLabel}>Brüt Ücret:</span>
                                    <span className={styles.infoValue}>{formatCurrency(formData.grossSalary)}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.infoLabel}>Kesintiler Toplamı:</span>
                                    <span className={styles.infoValue}>{formatCurrency(formData.totalDeductions)}</span>
                                </div>
                                <div className={styles.infoItem}>
                                    <span className={styles.infoLabel}>Toplam İşveren Maliyeti:</span>
                                    <span className={styles.infoValue}>{formatCurrency(formData.suggestedCost)}</span>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
                
                <div className={styles.formActions}>
                    <button 
                        type="button" 
                        className={styles.buttonCancel} 
                        onClick={onClose}
                    >
                        İptal
                    </button>
                    <button
                        type="submit"
                        form="add-salary-form"
                        className={styles.buttonSave}
                    >
                        <FaSave className={styles.buttonIcon} />
                        {editingSalary ? 'Güncelle' : 'Kaydet'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddSalaryModal;
